// The games UI is hash-routed, so filters live after the `#` (`/#/?opening=Foo`). A query on the path
// (`/?opening=Foo`), which is what people type and what URL-normalizing tools produce, would open the
// app unfiltered. So before the router is built, lift the app's own params from the path query into
// the hash. Runs once at module load; a no-op on normal in-app URLs.

// Only these move. Everything else (utm_*, gclid...) stays on the path, where analytics reads it.
// Keep in sync with getGamesUrl / GamesContainer's urlParams.
const APP_PARAMS = [
  'username',
  // The home band submits the handle under this name so browsers don't offer saved passwords.
  'handle',
  'group',
  'color',
  'result',
  'platform',
  'opening',
  'timeControl',
  'from',
  'to',
  'ratingFrom',
  'ratingTo',
  'pattern',
  'speed',
  'repair',
  'pgn',
  'onlyStarred',
  'favoriteMoves',
  'hasComments',
  'commentedByMe',
  'page',
  'gameId',
];

// Server-rendered pages that host the SPA on a real, linkable path (`/try?username=foo`), mapped to
// the hash route to open. Keep in sync with App.tsx's routes and the Razor page that mounts #app.
const PATH_ROUTES: Record<string, string> = {
  '/try': '/try',
};

export default function adoptPathQuery() {
  if (typeof window === 'undefined') {
    return;
  }

  const cleanPath = window.location.pathname.replace(/\/+$/, '').toLowerCase() || '/';
  // /blunder/{site}/{username} matches by prefix; the page passes the handle through data attributes.
  const pathRoute = cleanPath.startsWith('/blunder/') ? '/blunder-share' : PATH_ROUTES[cleanPath];
  if (!window.location.search && pathRoute == null) {
    return;
  }

  const pathQuery = new URLSearchParams(window.location.search);
  const moving = APP_PARAMS.filter((k) => pathQuery.has(k));
  if (moving.length === 0 && pathRoute == null) {
    return;
  }

  // Split the hash into its route and its own query: '#/?a=1' -> '/' + 'a=1'.
  const hash = window.location.hash.replace(/^#/, '');
  const cut = hash.indexOf('?');
  // An existing hash route wins; the path only supplies the route on first load.
  const route = (cut === -1 ? hash : hash.slice(0, cut)) || pathRoute || '';
  const hashQuery = new URLSearchParams(cut === -1 ? '' : hash.slice(cut + 1));

  // The hash wins on conflicts: it came from in-app navigation, the path copy is stale.
  moving.forEach((key) => {
    if (!hashQuery.has(key)) {
      hashQuery.set(key, pathQuery.get(key)!);
    }
    pathQuery.delete(key);
  });

  const rest = pathQuery.toString();
  const nextSearch = rest ? `?${rest}` : '';
  const nextQuery = hashQuery.toString();
  const nextHash = `#${route || '/'}${nextQuery ? `?${nextQuery}` : ''}`;

  // replaceState, so the correction doesn't cost a back-button step.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${nextSearch}${nextHash}`,
  );
}
